import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PublicHeader from './PublicHeader';
import { supabase } from './supabaseClient';
import { setCustomerSession } from './customerSession';
import './PublicHome.css';
import './CustomerAuth.css';

function parseRpcResult(data) {
  if (data == null) return null;
  if (typeof data === 'object' && !Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

function CustomerSignUp() {
  const navigate = useNavigate();
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (!supabase) {
      setError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return;
    }

    setBusy(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('register_customer', {
        p_full_name: fullName.trim(),
        p_email: email.trim(),
        p_password: password,
      });

      if (rpcError) throw rpcError;

      const row = parseRpcResult(data);
      if (!row || !row.ok) {
        setError(row?.error || 'Could not create your account.');
        return;
      }

      setCustomerSession({
        userId: row.user_id,
        email: row.email,
        fullName: row.full_name,
      });
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err?.message ||
          'Could not create your account. Run sql/add_customer_accounts_supabase.sql in Supabase.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ph-root ct-page">
      <PublicHeader />

      <main className="ca-page">
        <div className="ca-card">
          <h1 className="ca-title">Create an account</h1>
          <p className="ca-lead">
            Sign up as a customer to track enquiries and hear about new listings.
          </p>

          <form className="ca-form" onSubmit={handleSubmit}>
            {error ? (
              <p className="ca-error" role="alert">
                {error}
              </p>
            ) : null}
            <div className="ca-field">
              <label className="ca-label" htmlFor="ca-signup-name">
                Full name
              </label>
              <input
                id="ca-signup-name"
                className="ca-input"
                type="text"
                autoComplete="name"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Smith"
              />
            </div>

            <div className="ca-field">
              <label className="ca-label" htmlFor="ca-signup-email">
                Email
              </label>
              <input
                id="ca-signup-email"
                className="ca-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>

            <div className="ca-field">
              <label className="ca-label" htmlFor="ca-signup-password">
                Password
              </label>
              <input
                id="ca-signup-password"
                className="ca-input"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
              />
            </div>

            <div className="ca-field">
              <label className="ca-label" htmlFor="ca-signup-confirm">
                Confirm password
              </label>
              <input
                id="ca-signup-confirm"
                className="ca-input"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat password"
              />
            </div>

            <button type="submit" className="ph-btn ph-btn-primary ca-submit" disabled={busy}>
              {busy ? 'Creating account…' : 'Sign up'}
            </button>
          </form>

          <p className="ca-footer">
            Already have an account?{' '}
            <Link to="/sign-in" className="ca-footer-link">
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default CustomerSignUp;
